import { Fragment, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  cn,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@eureca/ui';
import {
  ConsultantCopyableId,
  ConsultantFilterTrigger,
  ConsultantLayout,
  ConsultantListHeader,
  ConsultantListPlaceholder,
  ConsultantPageTitle,
  ConsultantSearchInput,
  ConsultantTablePagination,
  ConsultantTableSection,
} from '@/shared/consultant-layout';
import { JobConfirmDialog } from './JobConfirmDialog';
import { JobsListSkeleton } from './JobsStates';
import {
  ArrowDownUpIcon,
  BanIcon,
  FilePenIcon,
  FlagIcon,
  MoreHorizontalIcon,
  PlusIcon,
  RadioIcon,
  RouteIcon,
  TriangleAlertIcon,
} from './icons';

export type JobStatus = 'ativa' | 'ativa-privada' | 'rascunho' | 'encerrada' | 'cancelada';

export type JobStatusFilter = JobStatus;

export interface JobListItem {
  id: string;
  name: string;
  programId: string;
  programName: string;
  status: JobStatus;
  registrations: number;
  finalPhaseCompletions: number | null;
  startDate: string;
  /**
   * EU-424 RN-02/RN-03 — days remaining until the application window closes
   * (`endApplying − today`, inclusive BRT count via `computeProgramDaysRemaining`,
   * the same helper the Programs list uses). `null` when the job has no end
   * date → no badge. The column is still labeled "Início", but the badge counts
   * to the end.
   */
  endDaysRemaining: number | null;
  endDate: string;
}

// EU-408 — link privado da vaga (página do app candidato): `open-private-link`
// abre em nova guia (prévia) e `copy-private-link` copia para compartilhar com
// a empresa contratante revisar antes do lançamento. Só aparece em vaga
// publicada como privada.
export type JobAction =
  | 'duplicate'
  | 'edit'
  | 'end'
  | 'cancel'
  | 'open-private-link'
  | 'copy-private-link';

export type JobActiveDialog = 'end' | 'cancel' | 'bulk-end' | 'bulk-cancel' | null;

export interface ProgramFilterOption {
  id: string;
  name: string;
}

export interface JobsPageProps {
  jobs: JobListItem[];
  /** Workspace logo node (selected company) rendered next to the page title. */
  brand?: ReactNode;
  totalCount: number;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onCreateJob: () => void;
  onSortToggle: () => void;
  onOpenJob: (id: string) => void;
  onJobAction: (action: JobAction, id: string) => void;
  programOptions: ProgramFilterOption[];
  /** DEV-315 — um programa por vez; `null` = nenhum (a listagem não busca vagas). */
  selectedProgramId: string | null;
  /**
   * VPG-011 — as vagas do programa selecionado ainda não chegaram. Só a área da
   * lista vira esqueleto; a barra de filtros segue interativa.
   */
  jobsLoading: boolean;
  onProgramSelect: (programId: string) => void;
  /** Popover do filtro Programa — controlado pelo container (o placeholder também o abre). */
  programFilterOpen: boolean;
  onProgramFilterOpenChange: (open: boolean) => void;
  selectedStatuses: JobStatusFilter[];
  onStatusFilterToggle: (status: JobStatusFilter) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  rowsPerPage: number;
  rowsPerPageOptions: number[];
  onRowsPerPageChange: (rows: number) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  selectedIds: string[];
  onToggleSelectAll: () => void;
  onToggleSelectRow: (id: string) => void;
  onClearSelection: () => void;
  onBulkAction: (action: 'end' | 'cancel') => void;
  activeDialog: JobActiveDialog;
  onDialogOpenChange: (open: boolean) => void;
  onConfirmEnd: () => void;
  onConfirmCancel: () => void;
  onConfirmBulkEnd: () => void;
  onConfirmBulkCancel: () => void;
}

// Plural conforme literal da AC EU-155 (JRN-004). "Ativa e privada" mantido
// como linha separada por decisão registrada em
// specs/030-recruiter-journeys/research.md §1 — pode ser dobrada
// sob "Ativas" se PO assim definir (mudança de ~4 linhas).
const STATUS_FILTER_LABELS: Record<JobStatusFilter, string> = {
  ativa: 'Ativas',
  'ativa-privada': 'Ativas e priv.',
  rascunho: 'Rascunhos',
  encerrada: 'Encerradas',
  cancelada: 'Canceladas',
};

const STATUS_FILTER_ORDER: JobStatusFilter[] = [
  'ativa',
  'ativa-privada',
  'rascunho',
  'encerrada',
  'cancelada',
];

export function JobsPage({
  jobs,
  brand,
  totalCount,
  searchQuery,
  onSearchChange,
  onCreateJob,
  onSortToggle,
  onOpenJob,
  onJobAction,
  programOptions,
  selectedProgramId,
  jobsLoading,
  onProgramSelect,
  programFilterOpen,
  onProgramFilterOpenChange,
  selectedStatuses,
  onStatusFilterToggle,
  hasActiveFilters,
  onClearFilters,
  rowsPerPage,
  rowsPerPageOptions,
  onRowsPerPageChange,
  page,
  totalPages,
  onPageChange,
  selectedIds,
  onToggleSelectAll,
  onToggleSelectRow,
  onClearSelection,
  onBulkAction,
  activeDialog,
  onDialogOpenChange,
  onConfirmEnd,
  onConfirmCancel,
  onConfirmBulkEnd,
  onConfirmBulkCancel,
}: JobsPageProps) {
  const allRowsSelected = jobs.length > 0 && jobs.every((j) => selectedIds.includes(j.id));
  const anyRowsSelected = jobs.some((j) => selectedIds.includes(j.id));
  const headerCheckboxState: boolean | 'indeterminate' = allRowsSelected
    ? true
    : anyRowsSelected
      ? 'indeterminate'
      : false;

  const selectedJobs = jobs.filter((j) => selectedIds.includes(j.id));
  const bulkAvailableActions = computeBulkActions(selectedJobs.map((j) => j.status));

  return (
    <>
      <ConsultantLayout activeMenuId="jobs">
        <ConsultantLayout.Header>
          <ConsultantListHeader
            title={
              <ConsultantPageTitle icon={<RouteIcon className="size-4" />} brand={brand}>
                Vagas
              </ConsultantPageTitle>
            }
            actions={
              <Button onClick={onCreateJob}>
                <PlusIcon />
                Criar vaga
              </Button>
            }
            filters={
              <>
                <ProgramFilterButton
                  options={programOptions}
                  selected={selectedProgramId}
                  onSelect={onProgramSelect}
                  open={programFilterOpen}
                  onOpenChange={onProgramFilterOpenChange}
                />
                <StatusFilterButton selected={selectedStatuses} onToggle={onStatusFilterToggle} />
              </>
            }
            tools={
              <>
                <ConsultantSearchInput
                  value={searchQuery}
                  onChange={onSearchChange}
                  ariaLabel="Buscar vagas"
                  placeholder="Busque por ID ou nome"
                />
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Ordenar vagas"
                  onClick={onSortToggle}
                >
                  <ArrowDownUpIcon />
                </Button>
              </>
            }
          />
        </ConsultantLayout.Header>

        <ConsultantLayout.Body>
          {selectedProgramId === null ? (
            <ProgramRequiredState onSelectProgram={() => onProgramFilterOpenChange(true)} />
          ) : jobsLoading ? (
            <JobsListSkeleton />
          ) : totalCount === 0 ? (
            <EmptyState
              emptyMode={
                hasActiveFilters || searchQuery.trim().length > 0 ? 'no-results' : 'no-data'
              }
              onClearFilters={onClearFilters}
              onCreateJob={onCreateJob}
            />
          ) : (
            <ConsultantTableSection
              ariaLabel="Lista de vagas"
              pagination={
                <ConsultantTablePagination
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={rowsPerPageOptions}
                  onRowsPerPageChange={onRowsPerPageChange}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                  data-testid="jobs-list-pagination"
                />
              }
            >
              <Table
                className="table-fixed"
                containerClassName="flex-1 overflow-auto"
                storageId="recruiter:jobs-list"
              >
                <TableHeader className="sticky top-0 z-10 border-b border-border bg-card">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-12">
                      <Checkbox
                        aria-label="Selecionar todas as vagas da página"
                        checked={headerCheckboxState}
                        onCheckedChange={onToggleSelectAll}
                      />
                    </TableHead>
                    <TableHead resizable colKey="id" className="w-[80px]">
                      ID
                    </TableHead>
                    <TableHead resizable colKey="name">
                      Nome
                    </TableHead>
                    <TableHead resizable colKey="program">
                      Programa
                    </TableHead>
                    <TableHead resizable colKey="status">
                      Status
                    </TableHead>
                    <TableHead resizable colKey="registrations">
                      Inscritos / Finaliz. fase atual
                    </TableHead>
                    <TableHead resizable colKey="start">
                      Início
                    </TableHead>
                    <TableHead resizable colKey="end">
                      Fim
                    </TableHead>
                    <TableHead className="w-12">
                      <span className="sr-only">Ações</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.map((job) => (
                    <JobRow
                      key={job.id}
                      job={job}
                      selected={selectedIds.includes(job.id)}
                      onToggleSelect={() => onToggleSelectRow(job.id)}
                      onOpen={() => onOpenJob(job.id)}
                      onAction={(action) => onJobAction(action, job.id)}
                    />
                  ))}
                </TableBody>
              </Table>
            </ConsultantTableSection>
          )}
        </ConsultantLayout.Body>

        {selectedIds.length > 0 ? (
          <ConsultantLayout.Footer className="bg-muted shadow-[0_-4px_8px_-2px_rgba(33,41,40,0.05)]">
            <BulkActionBar
              count={selectedIds.length}
              availableActions={bulkAvailableActions}
              onAction={onBulkAction}
              onClearSelection={onClearSelection}
            />
          </ConsultantLayout.Footer>
        ) : null}
      </ConsultantLayout>

      <JobConfirmDialog
        open={activeDialog === 'end'}
        onOpenChange={onDialogOpenChange}
        title="Encerrar vaga"
        description="Deseja encerrar essa vaga? Ela deixará de receber novas inscrições e não poderá ser reaberta."
        cancelLabel="Voltar"
        confirmLabel="Encerrar"
        onConfirm={onConfirmEnd}
      />
      <JobConfirmDialog
        open={activeDialog === 'cancel'}
        onOpenChange={onDialogOpenChange}
        title="Cancelar vaga"
        description="Deseja cancelar essa vaga? Ela deixará de receber novas inscrições e não poderá ser reaberta."
        cancelLabel="Voltar"
        confirmLabel="Fazer cancelamento"
        onConfirm={onConfirmCancel}
      />
      <JobConfirmDialog
        open={activeDialog === 'bulk-end'}
        onOpenChange={onDialogOpenChange}
        title="Encerrar vagas selecionadas"
        description={`Deseja encerrar ${formatCount(selectedIds.length)}? Elas deixarão de receber novas inscrições e não poderão ser reabertas.`}
        cancelLabel="Voltar"
        confirmLabel="Encerrar"
        onConfirm={onConfirmBulkEnd}
      />
      <JobConfirmDialog
        open={activeDialog === 'bulk-cancel'}
        onOpenChange={onDialogOpenChange}
        title="Cancelar vagas selecionadas"
        description={`Deseja cancelar ${formatCount(selectedIds.length)}? Elas deixarão de receber novas inscrições e não poderão ser reabertas.`}
        cancelLabel="Voltar"
        confirmLabel="Fazer cancelamento"
        onConfirm={onConfirmBulkCancel}
      />
    </>
  );
}

function formatCount(count: number): string {
  if (count === 1) return '1 vaga';
  return `${String(count).padStart(2, '0')} vagas`;
}

interface ProgramFilterButtonProps {
  options: ProgramFilterOption[];
  selected: string | null;
  onSelect: (programId: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function ProgramFilterButton({
  options,
  selected,
  onSelect,
  open,
  onOpenChange,
}: ProgramFilterButtonProps) {
  // Combobox-style picker — search at the top, list below (Figma
  // TtLanTOfTFc8bHrQKgkalW node 17020:6825). Search is local; cmdk filters
  // `value={option.name}` against the typed query, restricted to the options
  // the container already scoped to the company (DEV-315 AC-3).
  // DEV-315 — SINGLE-select: a listagem busca as vagas de UM programa por vez,
  // então escolher substitui o anterior e o container fecha o popover.
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <ConsultantFilterTrigger label="Programa" count={selected ? 1 : 0} />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[400px] p-0">
        <Command>
          <CommandInput placeholder="Busque por programas" />
          {/* `p-1` on the list + `rounded-md` on each item matches the Figma
              "List group" spec (node 17020:6825): selected items render as a
              pill inset from the popover edge, so the background never reaches
              the popover's rounded corners and no diagonal seam shows. */}
          <CommandList className="max-h-[300px] p-1">
            <CommandEmpty>Nenhum programa encontrado</CommandEmpty>
            {options.map((option) => {
              const isSelected = selected === option.id;
              return (
                <CommandItem
                  key={option.id}
                  value={option.name}
                  onSelect={() => onSelect(option.id)}
                  data-active={isSelected || undefined}
                  className={cn(
                    'flex items-center justify-between gap-2 rounded-md px-2 py-1.5',
                    isSelected && 'bg-secondary/40'
                  )}
                >
                  <span className="truncate">{option.name}</span>
                  {isSelected ? (
                    <span aria-hidden className="size-2 shrink-0 rounded-full bg-primary" />
                  ) : null}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface StatusFilterButtonProps {
  selected: JobStatusFilter[];
  onToggle: (status: JobStatusFilter) => void;
}

function StatusFilterButton({ selected, onToggle }: StatusFilterButtonProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ConsultantFilterTrigger label="Status" count={selected.length} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[240px] p-2">
        <p className="px-2 pb-2 text-xs font-medium text-muted-foreground">Filtrar por status</p>
        <DropdownMenuSeparator />
        <DropdownMenuGroup className="py-1">
          {STATUS_FILTER_ORDER.map((status) => (
            <FilterCheckRow
              key={status}
              checked={selected.includes(status)}
              onToggle={() => onToggle(status)}
              label={STATUS_FILTER_LABELS[status]}
            />
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface FilterCheckRowProps {
  checked: boolean;
  onToggle: () => void;
  label: string;
}

function FilterCheckRow({ checked, onToggle, label }: FilterCheckRowProps) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      onClick={onToggle}
      className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground outline-none transition-colors hover:bg-accent focus-visible:bg-accent"
    >
      <Checkbox checked={checked} tabIndex={-1} className="pointer-events-none" />
      <span className="flex-1 truncate">{label}</span>
    </button>
  );
}

interface JobRowProps {
  job: JobListItem;
  selected: boolean;
  onToggleSelect: () => void;
  onOpen: () => void;
  onAction: (action: JobAction) => void;
}

function JobRow({ job, selected, onToggleSelect, onOpen, onAction }: JobRowProps) {
  return (
    <TableRow className={cn(selected && 'bg-secondary/30')}>
      <TableCell>
        <Checkbox
          aria-label={`Selecionar vaga ${job.name}`}
          checked={selected}
          onCheckedChange={onToggleSelect}
        />
      </TableCell>
      <TableCell>
        <ConsultantCopyableId value={job.id} label="ID da vaga" />
      </TableCell>
      <TableCell>
        <button
          type="button"
          onClick={onOpen}
          className="text-left text-sm font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
        >
          {job.name}
        </button>
      </TableCell>
      <TableCell className="text-muted-foreground">{job.programName}</TableCell>
      <TableCell>
        <StatusBadge status={job.status} />
      </TableCell>
      <TableCell className="tabular-nums text-muted-foreground">
        <RegistrationsCell job={job} />
      </TableCell>
      <TableCell className="tabular-nums text-muted-foreground">
        <StartDateCell job={job} />
      </TableCell>
      <TableCell className="tabular-nums text-muted-foreground">{job.endDate}</TableCell>
      <TableCell className="text-right">
        <JobRowActions job={job} onAction={onAction} />
      </TableCell>
    </TableRow>
  );
}

function RegistrationsCell({ job }: { job: JobListItem }) {
  if (job.finalPhaseCompletions == null) {
    // RN-03 / FR-003: em-dash (U+2014), não hyphen-minus.
    return <span>—</span>;
  }
  return (
    <span>
      {job.registrations.toLocaleString('pt-BR')}
      <span className="px-1 font-medium text-foreground">/</span>
      {job.finalPhaseCompletions.toLocaleString('pt-BR')}
    </span>
  );
}

function StartDateCell({ job }: { job: JobListItem }) {
  if (!job.startDate) {
    return <span>—</span>;
  }
  return (
    <span>
      {job.startDate}
      {job.endDaysRemaining != null ? (
        <>
          <span className="px-1">-</span>
          <span className="font-medium text-foreground">{job.endDaysRemaining} dias</span>
        </>
      ) : null}
    </span>
  );
}

interface JobRowActionsProps {
  job: JobListItem;
  onAction: (action: JobAction) => void;
}

interface MenuOption {
  action: JobAction;
  label: string;
  destructive?: boolean;
}

function getMenuGroupsForStatus(status: JobStatus): MenuOption[][] {
  if (status === 'ativa') {
    return [
      [
        { action: 'duplicate', label: 'Duplicar' },
        { action: 'edit', label: 'Editar' },
        { action: 'end', label: 'Encerrar' },
      ],
      [{ action: 'cancel', label: 'Cancelar', destructive: true }],
    ];
  }
  if (status === 'ativa-privada') {
    // EU-244 — a publicação acontece SÓ no nível do programa; a ação de
    // publicar/ativar a vaga isoladamente foi removida.
    // EU-408 — vaga privada expõe "Copiar link privado" para compartilhar a
    // prévia com a empresa contratante.
    return [
      [
        { action: 'open-private-link', label: 'Acessar link privado' },
        { action: 'copy-private-link', label: 'Copiar link privado' },
        { action: 'duplicate', label: 'Duplicar' },
        { action: 'edit', label: 'Editar' },
        { action: 'end', label: 'Encerrar' },
      ],
      [{ action: 'cancel', label: 'Cancelar', destructive: true }],
    ];
  }
  if (status === 'rascunho') {
    // EU-244 — sem ação de publicar vaga (publicação é só no programa).
    return [
      [
        { action: 'duplicate', label: 'Duplicar' },
        { action: 'edit', label: 'Editar' },
      ],
      [{ action: 'cancel', label: 'Cancelar', destructive: true }],
    ];
  }
  // Terminal statuses (encerrada/cancelada): only Duplicar — once a job is
  // ended/cancelled the recruiter's main move is to clone it as a new draft.
  return [[{ action: 'duplicate', label: 'Duplicar' }]];
}

function JobRowActions({ job, onAction }: JobRowActionsProps) {
  const groups = getMenuGroupsForStatus(job.status);
  if (groups.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Ações para ${job.name}`}
          className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-border/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 data-[state=open]:bg-border/50 data-[state=open]:text-foreground"
        >
          <MoreHorizontalIcon className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[180px]">
        {groups.map((group, groupIndex) => (
          <Fragment key={groupIndex}>
            {groupIndex > 0 && <DropdownMenuSeparator />}
            <DropdownMenuGroup>
              {group.map((option) => (
                <DropdownMenuItem
                  key={option.action}
                  destructive={option.destructive}
                  onClick={() => onAction(option.action)}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StatusBadge({ status }: { status: JobStatus }) {
  if (status === 'ativa') {
    return (
      <Badge className="bg-secondary text-primary" leftIcon={<RadioIcon className="size-3" />}>
        Ativa
      </Badge>
    );
  }
  if (status === 'ativa-privada') {
    return (
      <Badge className="bg-secondary text-primary" leftIcon={<RadioIcon className="size-3" />}>
        Ativa e priv.
      </Badge>
    );
  }
  if (status === 'rascunho') {
    return (
      <Badge
        className="bg-warning text-primary-foreground"
        leftIcon={<FilePenIcon className="size-3" />}
      >
        Rascunho
      </Badge>
    );
  }
  if (status === 'encerrada') {
    return (
      <Badge
        className="bg-primary text-primary-foreground"
        leftIcon={<FlagIcon className="size-3" />}
      >
        Encerrada
      </Badge>
    );
  }
  return (
    <Badge
      className="bg-destructive text-primary-foreground"
      leftIcon={<BanIcon className="size-3" />}
    >
      Cancelada
    </Badge>
  );
}

type BulkAction = 'end' | 'cancel';

function computeBulkActions(statuses: JobStatus[]): BulkAction[] {
  if (statuses.length === 0) return [];
  const sets = statuses.map((status) => allowedActionsFor(status));
  const intersection = sets.reduce<Set<BulkAction>>((acc, set, index) => {
    if (index === 0) return new Set(set);
    return new Set([...acc].filter((action) => set.has(action)));
  }, new Set());
  return Array.from(intersection);
}

function allowedActionsFor(status: JobStatus): Set<BulkAction> {
  if (status === 'ativa' || status === 'ativa-privada') {
    return new Set(['end', 'cancel']);
  }
  if (status === 'rascunho') {
    return new Set(['cancel']);
  }
  return new Set();
}

interface BulkActionBarProps {
  count: number;
  availableActions: BulkAction[];
  onAction: (action: BulkAction) => void;
  onClearSelection: () => void;
}

function BulkActionBar({
  count,
  availableActions,
  onAction,
  onClearSelection,
}: BulkActionBarProps) {
  const hasEnd = availableActions.includes('end');
  const hasCancel = availableActions.includes('cancel');
  const hasAny = hasEnd || hasCancel;

  return (
    <div
      role="region"
      aria-label="Ações em lote"
      className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <p className="text-base font-medium text-foreground sm:text-xl">
        {formatCount(count)} selecionada{count === 1 ? '' : 's'}
      </p>
      <div className="flex flex-row flex-wrap items-center gap-2">
        {hasEnd ? (
          <Button variant="outline" onClick={() => onAction('end')}>
            Encerrar
          </Button>
        ) : null}
        {hasCancel ? (
          <Button variant="outline" onClick={() => onAction('cancel')}>
            Cancelar
          </Button>
        ) : null}
        {!hasAny ? (
          <Button variant="outline" disabled>
            Sem ações disponíveis
          </Button>
        ) : null}
        <Button variant="outline" onClick={onClearSelection}>
          Limpar seleção
        </Button>
      </div>
    </div>
  );
}

interface ProgramRequiredStateProps {
  onSelectProgram: () => void;
}

/**
 * DEV-315 — sem programa selecionado a listagem NÃO busca vagas (a pessoa
 * consultora trabalha num programa por vez; pré-carregar a empresa inteira era
 * o que deixava o load lento). Decisão do PO (08/10/2026): placeholder pedindo
 * a escolha, não auto-seleção do primeiro programa.
 */
function ProgramRequiredState({ onSelectProgram }: ProgramRequiredStateProps) {
  return (
    <ConsultantListPlaceholder className="gap-4">
      <div className="flex size-12 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <RouteIcon className="size-6" />
      </div>
      <div className="flex flex-col gap-1" data-testid="jobs-list-program-required">
        <p className="text-2xl font-medium leading-none text-foreground">Selecione um programa</p>
        <p className="text-sm text-muted-foreground">
          A listagem mostra as vagas de um programa por vez. Escolha um programa no filtro para ver
          as vagas dele.
        </p>
      </div>
      <Button onClick={onSelectProgram}>Selecionar programa</Button>
    </ConsultantListPlaceholder>
  );
}

/**
 * Dois estados vazios distintos por AC EU-155 (JRN-002 + JRN-006):
 *
 * - `no-data`     — workspace sem nenhuma vaga cadastrada. CTA "Criar vaga".
 * - `no-results`  — filtro/busca não casou com nada. CTA "Limpar filtros".
 *
 * Antes era um único estado com copy genérico — corrigido para casar literal
 * da AC.
 */
interface EmptyStateProps {
  emptyMode: 'no-data' | 'no-results';
  onClearFilters: () => void;
  onCreateJob: () => void;
}

function EmptyState({ emptyMode, onClearFilters, onCreateJob }: EmptyStateProps) {
  const isNoData = emptyMode === 'no-data';
  return (
    <ConsultantListPlaceholder className="gap-4">
      <div className="flex size-12 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <TriangleAlertIcon className="size-6" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-2xl font-medium leading-none text-foreground">
          {isNoData ? 'Sem Vagas' : 'Nenhum resultado encontrado'}
        </p>
        <p className="text-sm text-muted-foreground">
          {isNoData
            ? 'Este programa ainda não tem nenhuma vaga cadastrada. Crie a primeira e comece a fazer processos seletivos.'
            : 'Experimente remover alguns filtros.'}
        </p>
      </div>
      {isNoData ? (
        <Button onClick={onCreateJob}>Criar Vaga</Button>
      ) : (
        <Button onClick={onClearFilters}>Limpar filtros</Button>
      )}
    </ConsultantListPlaceholder>
  );
}
